import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames/bind';
import subtractDays from 'date-fns/sub_days';

import {
  DataBlocks,
  Table,
  I18n,
  Link,
  ErrorBlock,
  DatePicker,
  Button,
  TabsList,
  Tab,
} from 'components';
import { SmartTable } from 'containers';

import { formatDateString, formatPrice } from 'utils/formatString';
import { printBytes } from 'utils/accumulators';

import {
  fetchInternetStats,
  fetchCashflowStats,
  updateInternetPeriodStart as updatePeriodStart,
  updateInternetPeriodEnd as updatePeriodEnd,
} from '../../actions';

import styles from './styles.pcss';
import { INTERNET_STATS_SECTIONS } from '../../constants';

const cx = classNames.bind(styles);

const formatDuration = (value) => {
  const days = Math.floor(value / 86400);
  const hours = Math.floor(value % 86400 / 3600);
  const minutes = Math.floor(value % 3600 / 60);
  const seconds = value % 60;
  return `
    ${days ? `${days} д.` : ''}
    ${hours ? `${hours} ч.` : ''}
    ${minutes ? `${minutes} мин.` : ''}
    ${seconds ? `${seconds} сек.` : ''}
  `;
};

class HomeInternetDetalization extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      activeTab: INTERNET_STATS_SECTIONS.TRAFFIC,
      customPeriodStart: null,
      customPeriodEnd: null,
    };
  }

  onCreateReport = () => {
    this.setState({
      customPeriodStart: this.props.period.start,
      customPeriodEnd: this.props.period.end,
    });
    this.props.actions.fetchInternetStats(this.props.period.start, this.props.period.end);
    if (this.props.cashflowStats) {
      this.props.actions.fetchCashflowStats(this.props.period.start, this.props.period.end);
    }
  };

  handleTab = (tab) => {
    this.setState({ activeTab: tab });
    if (!this.props.cashflowStats && tab === INTERNET_STATS_SECTIONS.CASHFLOW) {
      this.props.actions.fetchCashflowStats(
        this.state.customPeriodStart,
        this.state.customPeriodEnd,
      );
    }
  };

  refreshStats = () => {
    if (this.props.error) {
      this.props.actions.fetchInternetStats(
        this.state.customPeriodStart,
        this.state.customPeriodEnd,
      );
    }
    if (this.props.cashflowStatsFetchingError) {
      this.props.actions.fetchCashflowStats(
        this.state.customPeriodStart,
        this.state.customPeriodEnd,
      );
    }
  };

  renderInternetStatsSubTable = ({ sessionsData }) => (
    <div className={cx('subTableContainer')}>
      <Table
        className={cx('subTable')}
        data={sessionsData}
        config={{
          columns: [
            {
              dataField: 'startSessionTime',
              transformer: (row) => formatDateString(row.startSessionTime, 'DD.MM.YYYY HH:mm'),
              width: '20%',
            },
            {
              dataField: 'sessionDuration',
              transformer: (row) => formatDuration(row.sessionDuration),
              width: '35%',
            },
            {
              dataField: 'receivedData',
              transformer: (row) => printBytes(row.receivedData),
              width: '15%',
            },
            {
              dataField: 'transferredData',
              transformer: (row) => printBytes(row.transferredData),
              width: '15%',
            },
            {
              dataField: 'cost',
              transformer: (row) => `${formatPrice(row.cost)} ₽`,
              width: '15%',
            },
          ],
          uniqueIdField: 'startSessionTime',
        }}
      />
    </div>
  );

  render() {
    const {
      internetStats,
      cashflowStats,
      actions,
      isFetching,
      isCashflowStatsFetching,
      error,
      cashflowStatsFetchingError,
      period,
      isArchiveAvailable,
    } = this.props;

    const isOpened = !!(isFetching || (internetStats || internetStats === null));

    if (error || cashflowStatsFetchingError) {
      return (
        <ErrorBlock
          title={<I18n path="detalization.homeInternet.title" />}
          refreshAction={this.refreshStats}
        />
      );
    }

    return (
      <DataBlocks.Block
        title={isOpened ? (
          <I18n path="detalization.homeInternet.title" />
        ) : (
          <Link onClick={actions.fetchInternetStats}>
            <I18n path="detalization.homeInternet.title" />
          </Link>
        )}
        refreshAction={this.refreshStats}
        isLoading={isFetching || isCashflowStatsFetching}
      >
        {isOpened && (
          <>
            <div>
              <div className={cx('dateGroup')}>
                <span className={cx('dateLabel')}>
                  С
                </span>
                <DatePicker
                  className={cx('datePicker')}
                  value={period.start}
                  min={subtractDays(new Date(), 180)}
                  max={period.end}
                  onChange={this.props.actions.updatePeriodStart}
                />
              </div>
              <div className={cx('dateGroup')}>
                <span className={cx('dateLabel')}>
                  До
                </span>
                <DatePicker
                  className={cx('datePicker')}
                  value={period.end}
                  min={period.start}
                  max={new Date()}
                  onChange={this.props.actions.updatePeriodEnd}
                />
              </div>
              <div className={cx('button')}>
                <Button
                  onClick={this.onCreateReport}
                  disabled={!period.isDirty || !period.isValid}
                >
                  <I18n path="detalization.createReport" />
                </Button>
              </div>
            </div>

            {internetStats && internetStats.length ? (
              <>
                {isArchiveAvailable && (
                  <div className={cx('tabs')}>
                    <TabsList
                      type="segmented"
                      activeTab={this.state.activeTab}
                      handleTabClick={this.handleTab}
                    >
                      <Tab id={INTERNET_STATS_SECTIONS.TRAFFIC}>
                        <I18n path="detalization.homeInternet.traffic" />
                      </Tab>
                      <Tab id={INTERNET_STATS_SECTIONS.CASHFLOW}>
                        <I18n path="detalization.homeInternet.cashflow" />
                      </Tab>
                    </TabsList>
                  </div>
                )}
                {this.state.activeTab === INTERNET_STATS_SECTIONS.CASHFLOW ? (
                  <Table
                    data={cashflowStats}
                    config={{
                      columns: [
                        {
                          name: 'detalization.homeInternet.cashflowStatsTable.date',
                          dataField: 'dateTime',
                          transformer: (row) => formatDateString(row.dateTime, 'DD.MM.YYYY HH:mm'),
                        },
                        {
                          name: 'detalization.homeInternet.cashflowStatsTable.operation',
                          dataField: 'operation',
                        },
                        {
                          name: 'detalization.homeInternet.cashflowStatsTable.startBalance',
                          dataField: 'startBalance',
                          transformer: (row) => `${formatPrice(row.startBalance)} ₽`,
                        },
                        {
                          name: 'detalization.homeInternet.cashflowStatsTable.movementValue',
                          dataField: 'movementValue',
                          transformer: (row) => `${formatPrice(row.movementValue)} ₽`,
                        },
                        {
                          name: 'detalization.homeInternet.cashflowStatsTable.endBalance',
                          dataField: 'endBalance',
                          transformer: (row) => `${formatPrice(row.endBalance)} ₽`,
                        },
                      ],
                      uniqueIdField: 'dateTime',
                      nameAsTranslationKey: true,
                    }}
                  />
                ) : (
                  <SmartTable
                    pathToState="detalization.internetStats"
                    config={{
                      columns: [
                        {
                          name: 'detalization.homeInternet.internetStatsTable.date',
                          dataField: 'month',
                          expandable: true,
                          width: '20%',
                        },
                        {
                          name: 'detalization.homeInternet.internetStatsTable.duration',
                          dataField: 'sessionDuration',
                          transformer: (row) => formatDuration(row.sessionDuration),
                          width: '35%',
                        },
                        {
                          name: 'detalization.homeInternet.internetStatsTable.received',
                          dataField: 'receivedData',
                          transformer: (row) => printBytes(row.receivedData),
                          width: '15%',
                        },
                        {
                          name: 'detalization.homeInternet.internetStatsTable.transferred',
                          dataField: 'transferredData',
                          transformer: (row) => printBytes(row.transferredData),
                          width: '15%',
                        },
                        {
                          name: 'detalization.homeInternet.internetStatsTable.cost',
                          dataField: 'cost',
                          transformer: (row) => `${formatPrice(row.cost)} ₽`,
                          width: '15%',
                        },
                      ],
                      uniqueIdField: 'month',
                      expandField: 'expanded',
                      nameAsTranslationKey: true,
                    }}
                    expandRenderer={this.renderInternetStatsSubTable}
                  />
                )}
              </>
            ) : (
              <div className={cx('emptyMessage')}>
                <I18n path="detalization.homeInternet.emptyMessage" />
              </div>
            )}
          </>
        )}
      </DataBlocks.Block>
    );
  }
}

function mapStateToProps(state) {
  return {
    internetStats: state.detalization.internetStats.data,
    isArchiveAvailable: state.detalization.internetStats.isArchiveAvailable,
    isFetching: state.detalization.internetStats.isFetching,
    error: state.detalization.internetStats.error,
    cashflowStats: state.detalization.cashflowStats.data,
    isCashflowStatsFetching: state.detalization.cashflowStats.isFetching,
    cashflowStatsFetchingError: state.detalization.cashflowStats.error,
    period: state.detalization.internetPeriod,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      fetchInternetStats,
      fetchCashflowStats,
      updatePeriodStart,
      updatePeriodEnd,
    }, dispatch),
  };
}

HomeInternetDetalization.propTypes = {
  actions: PropTypes.object,
  internetStats: PropTypes.array,
  cashflowStats: PropTypes.array,
  isFetching: PropTypes.bool,
  isCashflowStatsFetching: PropTypes.bool,
  error: PropTypes.string,
  cashflowStatsFetchingError: PropTypes.string,
  period: PropTypes.object,
  isArchiveAvailable: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(HomeInternetDetalization);
